import THREE from './three-extra'
let commonFragment = THREE.ShaderChunk[ 'common' ]
let fogParsVertex = THREE.ShaderChunk['fog_pars_vertex']
let fogParsFragment = THREE.ShaderChunk['fog_pars_fragment']
let fogVertex = THREE.ShaderChunk['fog_vertex']
let fogFragment = THREE.ShaderChunk['fog_fragment']

// Displacement Shader
let dsVertex = `
  uniform float amplitude;
  attribute vec3 customColor;
  attribute vec3 displacement;

  varying vec3 vNormal;
  varying vec3 vColor;
  varying vec3 vPos;

  void main() {
    vNormal = normal;
    vColor = customColor;
    vPos = (modelMatrix * vec4(position, 1.0 )).xyz;
    vec3 newPosition = position + normal * amplitude * displacement;
    gl_Position = projectionMatrix * modelViewMatrix * vec4( newPosition, 1.0 );
  }`

let dsFragment = `
  uniform float opacity;
  uniform vec3 diffuse;
  varying vec3 vPos;
  varying vec3 vNormal;
  varying vec3 vColor;
  struct PointLight {
    vec3 position;
    vec3 color;
  };
  uniform PointLight pointLights[ NUM_POINT_LIGHTS ];

  void main() {
    vec4 addedLights = vec4(0.05, 0.05, 0.05, 0.5);
    for(int l = 0; l < NUM_POINT_LIGHTS; l++) {
      vec3 adjustedLight = pointLights[l].position + cameraPosition;
      vec3 lightDirection = normalize(vPos - adjustedLight);
      addedLights.rgb += clamp(dot(-lightDirection, vNormal), 0.0, 1.0) * pointLights[l].color;
    }
    gl_FragColor = mix(vec4(diffuse.x, diffuse.y, diffuse.z, opacity), vec4(vColor.x, vColor.y, vColor.z, opacity), addedLights);
    if ( gl_FragColor.a < 0.1 ) discard;
  }`

let displacementShader = {
  vertex: dsVertex,
  fragment: dsFragment
}

// GLSL textureless classic 2D noise "cnoise",
// with an RSL-style periodic variant "pnoise".
// Author:  Stefan Gustavson (stefan.gustavson@liu.se)
// Version: 2011-08-22
//
// Many thanks to Ian McEwan of Ashima Arts for the
// ideas for permutation and gradient selection.
//
// Distributed under the MIT license. See LICENSE file.
// https://github.com/ashima/webgl-noise

let noiseVertex = `
  vec4 mod289(vec4 x) {
    return x - floor(x * (1.0 / 289.0)) * 289.0;
  }

  vec4 permute(vec4 x) {
    return mod289(((x*34.0)+1.0)*x);
  }

  vec4 taylorInvSqrt(vec4 r) {
    return 1.79284291400159 - 0.85373472095314 * r;
  }

  vec2 fade(vec2 t) {
    return t*t*t*(t*(t*6.0-15.0)+10.0);
  }

  // Classic Perlin noise
  float cnoise(vec2 P) {
    vec4 Pi = floor(P.xyxy) + vec4(0.0, 0.0, 1.0, 1.0);
    vec4 Pf = fract(P.xyxy) - vec4(0.0, 0.0, 1.0, 1.0);
    Pi = mod289(Pi); // To avoid truncation effects in permutation
    vec4 ix = Pi.xzxz;
    vec4 iy = Pi.yyww;
    vec4 fx = Pf.xzxz;
    vec4 fy = Pf.yyww;

    vec4 i = permute(permute(ix) + iy);

    vec4 gx = fract(i * (1.0 / 41.0)) * 2.0 - 1.0 ;
    vec4 gy = abs(gx) - 0.5 ;
    vec4 tx = floor(gx + 0.5);
    gx = gx - tx;

    vec2 g00 = vec2(gx.x,gy.x);
    vec2 g10 = vec2(gx.y,gy.y);
    vec2 g01 = vec2(gx.z,gy.z);
    vec2 g11 = vec2(gx.w,gy.w);

    vec4 norm = taylorInvSqrt(vec4(dot(g00, g00), dot(g01, g01), dot(g10, g10), dot(g11, g11)));
    g00 *= norm.x;
    g01 *= norm.y;
    g10 *= norm.z;
    g11 *= norm.w;

    float n00 = dot(g00, vec2(fx.x, fy.x));
    float n10 = dot(g10, vec2(fx.y, fy.y));
    float n01 = dot(g01, vec2(fx.z, fy.z));
    float n11 = dot(g11, vec2(fx.w, fy.w));

    vec2 fade_xy = fade(Pf.xy);
    vec2 n_x = mix(vec2(n00, n01), vec2(n10, n11), fade_xy.x);
    float n_xy = mix(n_x.x, n_x.y, fade_xy.y);
    return 2.3 * n_xy;
  }

  float map(float value, float oldMin, float oldMax, float newMin, float newMax) {
    return newMin + (newMax - newMin) * (value - oldMin) / (oldMax - oldMin);
  }

  uniform float time;
  varying float vZ;
  ${fogParsVertex}

  void main() {
    vec3 newPos = position;
    vec2 peak = vec2(1.0 - abs(0.5 - uv.x), 1.0 - abs(0.5 - uv.y));
    vec2 noise = vec2(
      map(cnoise(vec2(0.3 * time + uv.x * 5.0, uv.y * 5.0)), 0.0, 1.0, -2.0, (peak.x * peak.y * 30.0)),
      map(cnoise(vec2(-0.3 * time + uv.x * 5.0, uv.y * 5.0)), 0.0, 1.0, -2.0, 25.0)
    );

    // newPos.x += noise.x * 0.5;
    newPos.z += noise.x * 0.06 * noise.y;
    vZ = newPos.z;
    vec4 mvPosition = modelViewMatrix * vec4( newPos, 1.0 );
    gl_PointSize = 15.0;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(newPos, 1.0);
    ${fogVertex}
  }`

let noiseFragment = `
  uniform float opacity;
  uniform vec3 customColor;
  uniform sampler2D texture;
  varying float vZ;
  ${commonFragment}
  ${fogParsFragment}

  float map( float value, float oldMin, float oldMax, float newMin, float newMax ) {
    return newMin + (newMax - newMin) * (value - oldMin) / (oldMax - oldMin);
  }

  void main() {
    float alpha = map( vZ / 2.0, -1.0 / 2.0, 30.0 / 2.0, 0.17, 1.0 );
    // vec3 color = vec3(0.1, 0.1, 0.1);

    gl_FragColor = vec4( customColor, alpha * opacity );
    gl_FragColor = gl_FragColor * texture2D( texture, gl_PointCoord );
    if ( gl_FragColor.a < 0.01 ) discard;
    ${fogFragment}
  }`

let noiseShader = {
  vertex: noiseVertex,
  fragment: noiseFragment
}

let starfieldVertex = `
  uniform float opacity;
  uniform float progress;
  uniform float sizeFactor;
  attribute float size;
  attribute float alpha;
  attribute vec3 nPosition;
  varying vec3 vColor;
  varying float vOpacity;
  
  void main() {
    vColor = color;
    float smoothProg = smoothstep( 0.0, 1.0, progress );
    vOpacity = mix( 0.5, alpha, smoothProg );
    // vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 );
    vec3 newPos = mix( position, nPosition, smoothProg );
    vec4 mvPosition = modelViewMatrix * vec4( newPos, 1.0 );
    gl_PointSize = size * ( sizeFactor / -mvPosition.z );
    gl_Position = projectionMatrix * mvPosition;
  }
`

let starfieldFragment = `
  uniform float opacity;
  uniform float progress;
  uniform sampler2D texture;
  varying float vOpacity;
  varying vec3 vColor;
  
  void main() {
    gl_FragColor = vec4( vColor, vOpacity * opacity );
    gl_FragColor = gl_FragColor * texture2D( texture, gl_PointCoord );
    if ( gl_FragColor.a < 0.3 ) discard;
  }
`

let starfieldShader = {
  vertex: starfieldVertex,
  fragment: starfieldFragment
}

export {
  displacementShader,
  noiseShader,
  starfieldShader
}



// WEBPACK FOOTER //
// ./src/lib/shaders.js